import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import './VolumeControl.scss';

// This component is "mixed", meaning it uses two or more different element types.
//
// The mute button needs to know what the previous volume was before audio was muted,
// and this information is controlled by the range input.
//
// This component is passed an `id` prop, which should be the id associated with the
// audio element we're controlling the volume for.

const VolumeControl = ({ audioRef, className }) => {
  const [volume, setVolume] = useState(1);
  const [prevVolume, setPrevVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const sliderThumbRef = useRef(null);
  const sliderFillRef = useRef(null);
  const audioEl = audioRef.current;
  const maxVol = 1;

  useEffect(() => {
    if (audioEl) {
      audioEl.volume = volume;
    } else {
      console.warn(
        'VolumeControl component: Volume control requires valid reference to an audio element.'
      );
    }
  }, [volume, audioEl]);

  const handleMute = () => {
    prevVolume > 0 && setMuted(!muted);
    muted ? setVolume(prevVolume) : setVolume(0);
  };

  const handleSetVolume = e => {
    const { valueAsNumber } = e.target;

    muted && valueAsNumber > 0 && setMuted(false);
    valueAsNumber === '0' && setMuted(true);
    setVolume(valueAsNumber);
    setPrevVolume(valueAsNumber);
  };

  useEffect(() => {
    if (sliderThumbRef.current && sliderFillRef.current) {
      const percentage = (volume / maxVol) * 100;

      sliderThumbRef.current.style.left = `${percentage}%`;
      sliderFillRef.current.style.width = `${percentage}%`;
    }
  }, [volume]);

  return (
    <div className={`volume-control__button-input-wrapper ${className}`}>
      <button
        className="text-btn btn__volume-state"
        type="button"
        aria-label={`${!muted ? 'mute' : 'unmute'}`}
        onClick={handleMute}
      >
        {!muted || volume > 0 ? (
          <i className="material-icons volume-btn" aria-hidden="true">
            volume_up
          </i>
        ) : (
          <i className="material-icons volume-btn" aria-hidden="true">
            volume_off
          </i>
        )}
      </button>
      <label
        htmlFor="rangeslider__volume-control"
        className="label__volume-control sr-only"
      >
        Volume
      </label>
      <div className={`volume-control__wrapper`}>
        {/* input is visually hidden */}
        <input
          name="Volume"
          className={`volume-controller`}
          type="range"
          id="rangeslider__volume-control"
          aria-valuenow={volume * 100}
          value={volume}
          min="0"
          max="1"
          step=".1"
          onChange={handleSetVolume}
        />
        {/* styled elements that mimic a range input */}
        <div className="custom-track" aria-hidden="true">
          <span ref={sliderFillRef} className="custom-fill"></span>
          <span ref={sliderThumbRef} className="custom-thumb"></span>
        </div>
      </div>
    </div>
  );
};

VolumeControl.propTypes = {
  audioRef: PropTypes.object,
  className: PropTypes.string,
};

export default VolumeControl;
